/**
 * DashboardTab Component
 * Platform-wide analytics dashboard for Super Admin
 */

import React, { useState } from 'react';
import {
  Building2,
  Users,
  Target,
  TrendingUp,
  UserCheck,
  Calendar,
  BarChart3,
  Activity,
} from 'lucide-react';
import { Card, LoadingSpinner } from '../../../components/common';
import { useDashboardAnalytics, useUsageTrends } from '../../../hooks/useSuperAdmin';

// Stat Card Component
interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ElementType;
  description?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color: 'blue' | 'green' | 'purple' | 'orange' | 'indigo' | 'pink';
}

const colorClasses = {
  blue: {
    bg: 'bg-primary-50',
    icon: 'bg-primary-100 text-primary-600',
    text: 'text-primary-600',
  },
  green: {
    bg: 'bg-success-50',
    icon: 'bg-success-100 text-success-600',
    text: 'text-success-600',
  },
  purple: {
    bg: 'bg-stone-100',
    icon: 'bg-stone-200 text-stone-700',
    text: 'text-stone-700',
  },
  orange: {
    bg: 'bg-warning-50',
    icon: 'bg-warning-100 text-warning-600',
    text: 'text-warning-600',
  },
  indigo: {
    bg: 'bg-primary-50',
    icon: 'bg-primary-100 text-primary-600',
    text: 'text-primary-600',
  },
  pink: {
    bg: 'bg-danger-50',
    icon: 'bg-danger-100 text-danger-600',
    text: 'text-danger-600',
  },
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon: Icon,
  description,
  trend,
  color,
}) => {
  const colors = colorClasses[color];
  return (
    <Card className={`p-6 ${colors.bg}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-stone-600">{title}</p>
          <p className="mt-2 text-3xl font-bold text-stone-900">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {description && (
            <p className="mt-1 text-sm text-stone-500">{description}</p>
          )}
          {trend && (
            <p
              className={`mt-2 text-sm font-medium ${
                trend.isPositive ? 'text-success-600' : 'text-danger-600'
              }`}
            >
              {trend.isPositive ? '+' : '-'}
              {Math.abs(trend.value)}% from last month
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colors.icon}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Card>
  );
};

// Distribution Chart Component
interface DistributionItem {
  label: string;
  value: number;
  color: string;
}

interface DistributionChartProps {
  title: string;
  items: DistributionItem[];
}

const DistributionChart: React.FC<DistributionChartProps> = ({ title, items }) => {
  const total = items.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-stone-900 mb-4">{title}</h3>
      <div className="space-y-4">
        {items.map((item, index) => {
          const percentage = total > 0 ? (item.value / total) * 100 : 0;
          return (
            <div key={index}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-stone-600 capitalize">{item.label}</span>
                <span className="font-medium text-stone-900">
                  {item.value} ({percentage.toFixed(1)}%)
                </span>
              </div>
              <div className="w-full bg-stone-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${item.color}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// Trend Chart Component (simple bar representation)
interface TrendChartProps {
  title: string;
  data: { date: string; count: number }[];
  color: string;
}

const TrendChart: React.FC<TrendChartProps> = ({ title, data, color }) => {
  if (!data || data.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-stone-900 mb-4">{title}</h3>
        <div className="text-center py-8 text-stone-500">No data available</div>
      </Card>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.count), 1);

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-stone-900 mb-4">{title}</h3>
      <div className="flex items-end gap-1 h-32">
        {data.slice(-30).map((item, index) => {
          const height = (item.count / maxValue) * 100;
          return (
            <div
              key={index}
              className="flex-1 group relative"
              title={`${item.date}: ${item.count}`}
            >
              <div
                className={`${color} rounded-t transition-all hover:opacity-80`}
                style={{ height: `${Math.max(height, 2)}%` }}
              />
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-stone-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                {item.date}: {item.count}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-2 text-xs text-stone-500">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </Card>
  );
};

export const DashboardTab: React.FC = () => {
  const [trendPeriod, setTrendPeriod] = useState('30d');

  const { data: analytics, isLoading: analyticsLoading, error: analyticsError } =
    useDashboardAnalytics();
  const { data: trends, isLoading: trendsLoading } = useUsageTrends(trendPeriod);

  if (analyticsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (analyticsError) {
    return (
      <Card className="p-8 text-center">
        <div className="text-danger-600 mb-2">Failed to load analytics</div>
        <p className="text-stone-500">Please try refreshing the page</p>
      </Card>
    );
  }

  if (!analytics) {
    return null;
  }

  // Prepare plan distribution data
  const planColors: Record<string, string> = {
    trial: 'bg-warning-500',
    basic: 'bg-primary-400',
    professional: 'bg-primary-500',
    enterprise: 'bg-stone-700',
  };

  const planDistribution: DistributionItem[] = analytics.planDistribution.map((p) => ({
    label: p.plan,
    value: p.count,
    color: planColors[p.plan] || 'bg-gray-500',
  }));

  // Prepare status distribution data
  const statusColors: Record<string, string> = {
    active: 'bg-success-500',
    suspended: 'bg-danger-500',
    trial: 'bg-yellow-500',
  };

  const statusDistribution: DistributionItem[] = analytics.statusDistribution.map((s) => ({
    label: s.status,
    value: s.count,
    color: statusColors[s.status] || 'bg-gray-500',
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Platform Analytics</h2>
        <p className="text-sm text-stone-500">
          Overview of platform health and usage metrics
        </p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Tenants"
          value={analytics.totalTenants}
          icon={Building2}
          description={`${analytics.newTenantsThisMonth} new this month`}
          color="blue"
        />
        <StatCard
          title="Active Tenants"
          value={analytics.activeTenants}
          icon={Activity}
          description={`${((analytics.activeTenants / analytics.totalTenants) * 100).toFixed(0)}% of total`}
          color="green"
        />
        <StatCard
          title="Total Users"
          value={analytics.totalUsers}
          icon={Users}
          description={`${analytics.newUsersThisMonth} new this month`}
          color="purple"
        />
        <StatCard
          title="Total OKRs"
          value={analytics.totalOKRs}
          icon={Target}
          color="orange"
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatCard
          title="Active Users (30 days)"
          value={analytics.activeUsersLast30Days}
          icon={UserCheck}
          description={`${((analytics.activeUsersLast30Days / analytics.totalUsers) * 100).toFixed(0)}% engagement rate`}
          color="indigo"
        />
        <StatCard
          title="This Month"
          value={`${analytics.newTenantsThisMonth} tenants, ${analytics.newUsersThisMonth} users`}
          icon={Calendar}
          description="New signups this month"
          color="pink"
        />
      </div>

      {/* Distribution Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <DistributionChart title="Plan Distribution" items={planDistribution} />
        <DistributionChart title="Status Distribution" items={statusDistribution} />
      </div>

      {/* Trend Charts */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-stone-400" />
            Usage Trends
          </h3>
          <select
            value={trendPeriod}
            onChange={(e) => setTrendPeriod(e.target.value)}
            className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>

        {trendsLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : trends ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <TrendChart
              title="Tenant Signups"
              data={trends.tenantSignups}
              color="bg-primary-500"
            />
            <TrendChart
              title="User Signups"
              data={trends.userSignups}
              color="bg-stone-600"
            />
            <TrendChart
              title="Daily Active Users"
              data={trends.dailyActiveUsers}
              color="bg-success-500"
            />
          </div>
        ) : null}
      </div>

      {/* Quick Stats Summary */}
      <Card className="p-6 bg-gradient-to-r from-sand to-primary-50">
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="w-6 h-6 text-primary-600" />
          <h3 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Quick Summary</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div className="bg-white/60 rounded-xl p-4">
            <p className="text-2xl font-bold text-primary-600">
              {analytics.totalUsers > 0
                ? (analytics.totalOKRs / analytics.totalUsers).toFixed(1)
                : 0}
            </p>
            <p className="text-sm text-stone-600">OKRs per User</p>
          </div>
          <div className="bg-white/60 rounded-xl p-4">
            <p className="text-2xl font-bold text-stone-700">
              {analytics.totalTenants > 0
                ? (analytics.totalUsers / analytics.totalTenants).toFixed(1)
                : 0}
            </p>
            <p className="text-sm text-stone-600">Users per Tenant</p>
          </div>
          <div className="bg-white/60 rounded-xl p-4">
            <p className="text-2xl font-bold text-success-600">
              {analytics.totalUsers > 0
                ? ((analytics.activeUsersLast30Days / analytics.totalUsers) * 100).toFixed(0)
                : 0}%
            </p>
            <p className="text-sm text-stone-600">30-day Engagement</p>
          </div>
          <div className="bg-white/60 rounded-xl p-4">
            <p className="text-2xl font-bold text-warning-600">
              {analytics.planDistribution.find((p) => p.plan === 'enterprise')?.count || 0}
            </p>
            <p className="text-sm text-stone-600">Enterprise Clients</p>
          </div>
        </div>
      </Card>
    </div>
  );
};
